import { useContext, useEffect, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';

import { AddPoints } from 'components/site/general/singleSelect/SelectedMarkerV2/AddPoints';
import { DragPoints } from 'components/site/general/singleSelect/SelectedMarkerV2/DragPoints';
import { MovePoint } from 'components/site/general/singleSelect/SelectedMarkerV2/MovePoint';
import { RemovePoints } from 'components/site/general/singleSelect/SelectedMarkerV2/RemovePoints';
import { SelectArea } from 'components/site/general/singleSelect/SelectedMarkerV2/SelectedArea';
import 'components/site/general/singleSelect/SelectedMarkerV2/style.css';
import { useCoordinatesActions } from 'components/site/general/singleSelect/SelectedMarkerV2/useCoordinatesActions';
import { ICoordinates } from 'libs/types/map/mapTypes';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    markerId: string;
    coordinates: ICoordinates[];
    minCoordinatesCount: number;
    maxCoordinatesCount?: number;
    isClosedPolygon: boolean;
    updateMarkerDirectly: (coordinates: ICoordinates[]) => void;
    updateMarkerGlobally: (coordinates: ICoordinates[]) => void;
    color: string;
}

const ADD_POINT_OFFSET_PX = 12;

export const SelectedMarkerV2Common = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const coordinateActions = useCoordinatesActions(ctxEdit.mapRef, props.coordinates);
    const [currentDrag, setCurrentDrag] = useState<'point' | 'full' | 'map' | null>(null);

    function getInitialXyCoordinates(): IXY[] {
        return props.coordinates.map((c) => ctxEdit.mapRef.current?.project([c.lng, c.lat]));
    }

    const [xyCoordinates, setXyCoordinates] = useState(() => getInitialXyCoordinates());
    useEffect(() => {
        const onMoveOrZoom = () => {
            setXyCoordinates(getInitialXyCoordinates());
            setCurrentDrag('map');
        };
        const onMoveEndOrZoomEnd = () => {
            setCurrentDrag(null);
        };
        ctxEdit.mapRef.current?.on('move', onMoveOrZoom);
        ctxEdit.mapRef.current?.on('zoom', onMoveOrZoom);
        ctxEdit.mapRef.current?.on('moveend', onMoveEndOrZoomEnd);
        ctxEdit.mapRef.current?.on('zoomend', onMoveEndOrZoomEnd);
        return () => {
            ctxEdit.mapRef.current?.off('move', onMoveOrZoom);
            ctxEdit.mapRef.current?.off('zoom', onMoveOrZoom);
            ctxEdit.mapRef.current?.off('moveend', onMoveEndOrZoomEnd);
            ctxEdit.mapRef.current?.off('zoomend', onMoveEndOrZoomEnd);
        };
    }, [ctxEdit.mapRef.current, props.coordinates]);

    useUpdateEffect(() => {
        setXyCoordinates(getInitialXyCoordinates());

        // Do not show the coordinates until the moment when the new coordinates
        // are in the component so points do not flash in old location
        setTimeout(() => {
            setCurrentDrag(null);
        }, 100);
    }, [props.coordinates]);

    const canAddAndRemovePoints = props.minCoordinatesCount !== props.maxCoordinatesCount;

    return (
        <SelectArea>
            <>
                {(currentDrag === null || currentDrag === 'full') && (
                    <MovePoint
                        xyCoordinates={xyCoordinates}
                        onDrag={(diffX, diffY) => {
                            if (!currentDrag) {
                                setCurrentDrag('full');
                            }
                            const newCoordinates = xyCoordinates
                                .map((c) =>
                                    ctxEdit.mapRef.current?.unproject([c.x + diffX, c.y + diffY])
                                )
                                .map(({ lat, lng }) => ({ lat, lng }));
                            props.updateMarkerDirectly(newCoordinates);
                        }}
                        onDragEnd={(diffX, diffY) => {
                            setXyCoordinates(xyCoordinates);

                            const newCoordinates = xyCoordinates
                                .map((c) =>
                                    ctxEdit.mapRef.current?.unproject([c.x + diffX, c.y + diffY])
                                )
                                .map(({ lat, lng }) => ({ lat, lng }));

                            props.updateMarkerDirectly(newCoordinates);
                            props.updateMarkerGlobally(newCoordinates);
                        }}
                    />
                )}
                {(currentDrag === null || currentDrag === 'point') && (
                    <DragPoints
                        xyCoordinates={xyCoordinates}
                        color={props.color}
                        onDrag={(xyCoordinate, index) => {
                            if (!currentDrag) {
                                setCurrentDrag('point');
                            }
                            props.updateMarkerDirectly(
                                coordinateActions.getForUpdate(xyCoordinate, index)
                            );
                        }}
                        onDragEnd={(xyCoordinate, index) => {
                            props.updateMarkerGlobally(
                                coordinateActions.getForUpdate(xyCoordinate, index)
                            );
                        }}
                    />
                )}
                {!currentDrag && canAddAndRemovePoints && (
                    <>
                        <AddPoints
                            xyCoordinates={
                                xyCoordinates.length === 2
                                    ? xyCoordinates.map((c) => ({
                                          x: c.x + ADD_POINT_OFFSET_PX,
                                          y: c.y + ADD_POINT_OFFSET_PX
                                      }))
                                    : xyCoordinates
                            }
                            isClosedPolygon={props.isClosedPolygon}
                            maxCoordinatesCount={props.maxCoordinatesCount}
                            color={props.color}
                            onNewPoint={(xyCoordinate, index) => {
                                props.updateMarkerGlobally(
                                    coordinateActions.getForAdd(xyCoordinate, index)
                                );
                            }}
                        />
                        <RemovePoints
                            xyCoordinates={xyCoordinates}
                            minCoordinatesCount={props.minCoordinatesCount}
                            color={props.color}
                            onPointRemove={(index) => {
                                props.updateMarkerGlobally(coordinateActions.getForRemove(index));
                            }}
                        />
                    </>
                )}
            </>
        </SelectArea>
    );
};
